import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { KeyRound, Factory, Wrench, Activity, Sparkles, LogOut, Loader2, ArrowLeft, Phone, Mail, Newspaper, MessageSquare, Gift, Coins, LayoutDashboard, Tag } from "lucide-react";
import { useVestaMe, ViewAsBar } from "./VestaViewAs";
import { InnerSlots, hasInner, DIALOGS } from "./VestaInner";
import { ChiaviMap, TecniciWide, useChiavi } from "./VestaChiavi";
import { BolletteReport } from "./VestaBollette";
import { VestaWorldLogo } from "./VestaWorldLogo";
import { getAs, useAsId } from "./VestaViewAs";
import { DIREZ } from "@/components/monitoring/shared";
import { TorriMonitoring } from "@/components/monitoring/TorriMonitoring";

const VWIcon = ({ className }) => <VestaWorldLogo className={`${className} origin-top-left scale-[1.94]`} />;
import { useAuth } from "@/context/AuthContext";
import { TorriLogo, TorriMark } from "@/components/TorriLogo";
import api from "@/lib/api";
import HearthVesta from "./HearthVesta";
import VestaInfo from "./VestaInfo";
import VestaVoice from "./VestaVoice";
import VestaNotifiche from "./VestaNotifiche";
import VestaInstall from "./VestaInstall";
import { voiceStore } from "./vestaStore";
import { COPPER } from "./VestaAuth";

export const SLOTS = [
  { key: "monitoring", title: "Torri Monitoring", desc: "La tua sala di controllo quotidiano.", icon: Activity },
  { key: "chiavi-in-mano", title: "Tools commerciali", desc: "La massima esperienza per il cliente finale.", icon: KeyRound },
  { key: "cassetta-attrezzi", title: "Tools tecnici", desc: "Elabora, studia e prepara il cantiere ora.", icon: Wrench },
  { key: "backoffice", title: "Backoffice con la fabbrica", desc: "Tutte le informazioni gestionali di cui hai bisogno.", icon: Factory },
  { key: "bacheca", title: "Vesta World", desc: "Un mondo innovativo dove si cresce condividendo.", icon: VWIcon, to: "/vesta/bacheca" },
  { key: "ai-vesta", title: "My Vesta", desc: "È la tua unicità che rende possibile il risultato della rete.", icon: Sparkles },
];

export function VestaParla() {
  useEffect(() => { voiceStore.open(); }, []);
  return <VestaHome />;
}

export function VestaGuard({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="flex h-screen items-center justify-center bg-black"><Loader2 className="h-6 w-6 animate-spin" style={{ color: COPPER }} /></div>;
  if (user === false) return <Navigate to={window.location.pathname === "/vesta/parla" ? "/vesta?next=/vesta/parla" : "/vesta"} replace />;
  if (!["partner_user", "admin", "head_of_partner", "back_office", "commercial_manager", "technical"].includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

export function VestaHeader() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const me = useVestaMe();
  return (
    <>
    <header className="flex items-center justify-between gap-3 border-b border-neutral-900 px-4 py-3 sm:px-10 sm:py-4" data-testid="vesta-header">
      <Link to="/vesta/home" className="flex shrink-0 items-center gap-4">
        <TorriMark className="h-9 w-9 sm:hidden" />
        <TorriLogo className="hidden sm:flex" imgClass="h-10" />
        <span className="hidden text-[11px] font-bold uppercase tracking-[0.25em] sm:inline" style={{ color: COPPER }}><span className="normal-case [animation:vestaBlink_3.2s_ease-in-out_infinite]">Ai VESTA</span></span>
      </Link>
      <div className="flex min-w-0 items-center gap-2 sm:gap-4">
        <div className="min-w-0 text-right leading-tight">
          <div className="truncate text-sm font-semibold text-white" data-testid="vesta-partner-name">{me?.partner?.name || ""}</div>
          <div className="truncate text-xs text-neutral-400" data-testid="vesta-user-name">{me?.user?.name || ""}</div>
        </div>
        {me?.admin !== undefined && (me.admin || me.viewing_as) && (
          <button type="button" onClick={() => nav("/")} data-testid="vesta-to-crm" title="Torna al gestionale"
            className="flex shrink-0 items-center gap-1.5 rounded-full border border-neutral-800 px-3 py-2 text-xs font-bold text-neutral-300 transition-colors duration-200 hover:border-[#C17F59] hover:text-[#C17F59]">
            <LayoutDashboard className="h-4 w-4" /><span className="hidden sm:inline">Gestionale</span>
          </button>
        )}
        <VestaNotifiche />
        <VestaInstall />
        <button type="button" onClick={async () => { await logout(); nav("/vesta"); }} data-testid="vesta-logout"
          className="shrink-0 rounded-full border border-neutral-800 p-2 text-neutral-400 transition-colors duration-200 hover:border-[#C17F59] hover:text-[#C17F59]" title="Esci">
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </header>
    <ViewAsBar me={me} />
    </>
  );
}

const PortalOverlay = ({ portal }) => {
  const [full, setFull] = useState(false);
  const r = portal.rect;
  useEffect(() => { const id = requestAnimationFrame(() => requestAnimationFrame(() => setFull(true))); return () => cancelAnimationFrame(id); }, []);
  const box = full ? portal.target : { top: r.top, left: r.left, width: r.width, height: r.height, borderRadius: 16 };
  return (
    <div className="fixed z-50 flex items-center justify-center overflow-hidden" data-testid="vesta-portal"
      style={{ ...box, background: "radial-gradient(circle at center, #C17F59 0%, #8A5537 55%, #2A160C 100%)", boxShadow: "0 0 36px -14px rgba(193,127,89,.55), inset 0 0 60px rgba(0,0,0,.35)", transition: "top .75s cubic-bezier(.7,0,.2,1), left .75s cubic-bezier(.7,0,.2,1), width .75s cubic-bezier(.7,0,.2,1), height .75s cubic-bezier(.7,0,.2,1), border-radius .75s ease" }}>
      <span className="absolute rounded-full blur-3xl" style={{ background: COPPER, width: full ? "160vmax" : 0, height: full ? "160vmax" : 0, opacity: full ? 0.22 : 0.6, transition: "width .9s ease-out, height .9s ease-out, opacity .9s ease-out" }} />
    </div>
  );
};

const SHORTCUTS = [["punti", "Punti", Coins], ["catalogo", "Catalogo Vesta", Gift]];
const SaldoShortcuts = () => {
  const [open, setOpen] = useState(null);
  return (
    <div className="ml-auto flex shrink-0 gap-2">
      {SHORTCUTS.map(([k, label, Icon]) => {
        const Dlg = DIALOGS[k];
        return (
          <div key={k}>
            <button type="button" onClick={() => setOpen(k)} data-testid={`vesta-shortcut-${k}`}
              className="flex flex-col items-center gap-1 rounded-xl border border-neutral-800 px-3 py-2 text-neutral-300 transition-colors hover:border-[#C17F59] hover:text-[#C17F59]">
              <Icon className="h-4 w-4" />
              <span className="text-[10px] font-bold uppercase tracking-wider">{label}</span>
            </button>
            <Dlg open={open === k} onOpenChange={(v) => setOpen(v ? k : null)} />
          </div>
        );
      })}
    </div>
  );
};

const WelcomeBar = () => {
  const me = useVestaMe();
  if (!me || me.admin || me.direzione) return null;
  const ref = me.referente;
  return (
    <div className="mb-10 grid gap-3 sm:grid-cols-2" data-testid="vesta-welcome">
      <div className="flex items-center gap-4 rounded-2xl border border-neutral-800 bg-neutral-950 px-5 py-4">
        <img src="/vesta-coin-rame.png" alt="" className="h-12 w-12" />
        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-neutral-400">Saldo Punti Vesta</div>
          <div className="font-heading text-2xl font-extrabold" style={{ color: COPPER }} data-testid="vesta-saldo">{(me.saldo_vesta ?? 0).toLocaleString("it-IT", { maximumFractionDigits: 1 })}</div>
        </div>
        <SaldoShortcuts />
      </div>
      {ref && (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-neutral-800 bg-neutral-950 px-5 py-4" data-testid="vesta-referente">
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-widest text-neutral-400">Il tuo referente Torri · {ref.ruolo}</div>
            <div className="truncate font-heading text-lg font-bold text-white" data-testid="vesta-referente-name">{ref.name}</div>
          </div>
          <div className="flex shrink-0 gap-2">
            {ref.phone && <a href={`tel:${ref.phone}`} className="rounded-full border border-neutral-800 p-2.5 text-neutral-300 transition-colors hover:border-[#C17F59] hover:text-[#C17F59]" data-testid="vesta-referente-tel" title={ref.phone}><Phone className="h-4 w-4" /></a>}
            {ref.email && <a href={`mailto:${ref.email}`} className="rounded-full border border-neutral-800 p-2.5 text-neutral-300 transition-colors hover:border-[#C17F59] hover:text-[#C17F59]" data-testid="vesta-referente-mail" title={ref.email}><Mail className="h-4 w-4" /></a>}
          </div>
        </div>
      )}
    </div>
  );
};

function BachecaNews({ wide }) {
  const [n, setN] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/vesta/bacheca/unread").then((r) => alive && setN(r.data)).catch(() => {});
    load(); const id = setInterval(load, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  if (!n) return null;
  const pill = "flex items-center gap-1 rounded-full px-2 py-1 sm:px-2.5 text-[10px] font-black uppercase tracking-widest text-black shadow-[0_0_16px_rgba(193,127,89,.6)]";
  const list = n.recent || (n.last ? [n.last] : []);
  const when = (iso) => {
    const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
    return m < 60 ? `${m} min` : m < 1440 ? `${Math.round(m / 60)} h` : new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
  };
  const src = (u) => (u?.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
  return (
    <>
      <div className="absolute right-3 top-3 z-10 flex items-center gap-1.5 sm:right-4 sm:top-4" data-testid="vesta-slot-bacheca-news">
        {n.posts > 0 && <span className={pill} style={{ background: COPPER }} data-testid="vesta-slot-bacheca-posts"><Newspaper className="h-3 w-3" />{n.posts > 99 ? "99+" : n.posts}<span className="hidden sm:inline"> {n.posts === 1 ? "nuovo post" : "nuovi post"}</span></span>}
        {n.dm > 0 && <span className={pill} style={{ background: "#fff" }} data-testid="vesta-slot-bacheca-dm"><MessageSquare className="h-3 w-3" />{n.dm > 99 ? "99+" : n.dm}<span className="hidden sm:inline"> {n.dm === 1 ? "messaggio" : "messaggi"}</span></span>}
      </div>
      {wide && !!list.length && (
        <div className="absolute bottom-3 right-3 top-10 z-10 flex w-[56%] flex-col justify-end gap-1 overflow-hidden sm:bottom-5 sm:right-6 sm:top-12 sm:w-[44%] sm:gap-1 lg:w-[36%]" data-testid="vesta-slot-bacheca-recent">
          {list.map((p, k) => (
            <div key={p.id} className={`flex shrink-0 items-center gap-2 rounded-lg border border-neutral-800 bg-black/60 px-1.5 py-1 backdrop-blur-md sm:gap-2.5 sm:rounded-xl sm:px-2 sm:py-1.5`} style={{ opacity: 1 - k * 0.18 }} data-testid={`vesta-slot-bacheca-post-${k}`}>
              {p.thumb ? <img src={src(p.thumb)} alt="" className="h-7 w-7 shrink-0 rounded-md object-cover sm:h-9 sm:w-9 sm:rounded-lg" />
                : <span className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-md bg-white sm:h-9 sm:w-9 sm:rounded-lg">
                    {p.author_foto || p.author_logo ? <img src={src(p.author_foto || p.author_logo)} alt="" className={`h-full w-full ${p.author_foto ? "object-cover" : "object-contain p-1"}`} /> : <Newspaper className="h-5 w-5 text-black" />}
                  </span>}
              <div className="min-w-0 flex-1 text-left">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-[10px] font-bold text-white sm:text-[11px]">{p.author?.staff ? p.author.name : p.author?.partner_name || p.author?.name}</span>
                  <span className="shrink-0 text-[10px] text-neutral-500">{when(p.created_at)}</span>
                </div>
                <div className="truncate text-[10px] leading-snug text-neutral-400 sm:text-[11px]">{p.testo || "Nuovo contenuto"}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

const ECG = "M0 20 L10 20.0 L18 16.0 L26 20.0 L34 20.0 L40 23.0 L48 2.0 L56 38.0 L63 14.0 L68 20.0 L84 20.0 L94 15.0 L104 20.0 L122 20 L132 20.0 L140 17.2 L148 20.0 L156 20.0 L162 22.1 L170 7.4 L178 32.6 L185 15.8 L190 20.0 L206 20.0 L216 16.5 L226 20.0 L256 20 L266 20.0 L274 16.0 L282 20.0 L290 20.0 L296 23.0 L304 2.0 L312 38.0 L319 14.0 L324 20.0 L340 20.0 L350 15.0 L360 20.0 L378 20 L388 20.0 L396 16.6 L404 20.0 L412 20.0 L418 22.6 L426 4.7 L434 35.3 L441 14.9 L446 20.0 L462 20.0 L472 15.8 L482 20.0 L512 20 L522 20.0 L530 16.0 L538 20.0 L546 20.0 L552 23.0 L560 2.0 L568 38.0 L575 14.0 L580 20.0 L596 20.0 L606 15.0 L616 20.0 L634 20 L644 20.0 L652 17.0 L660 20.0 L668 20.0 L674 22.2 L682 6.5 L690 33.5 L697 15.5 L702 20.0 L718 20.0 L728 16.2 L738 20.0 L768 20 L778 20.0 L786 16.2 L794 20.0 L802 20.0 L808 22.9 L816 2.9 L824 37.1 L831 14.3 L836 20.0 L852 20.0 L862 15.2 L872 20.0 L890 20 L900 20.0 L908 16.0 L916 20.0 L924 20.0 L930 23.0 L938 2.0 L946 38.0 L953 14.0 L958 20.0 L974 20.0 L984 15.0 L994 20.0 L1024 20 L1000 20";
const EcgLine = () => (
  <svg className="pointer-events-none absolute left-[2.9rem] right-[7.5rem] top-[1.15rem] h-8 sm:left-[3.8rem] sm:right-[8.75rem] sm:top-[1.625rem] sm:h-10" viewBox="0 0 1000 40" preserveAspectRatio="none" aria-hidden="true" data-testid="vesta-slot-monitoring-ecg">
    <path d={ECG} pathLength="1000" fill="none" stroke={COPPER} strokeWidth="1.6" strokeLinejoin="round" vectorEffect="non-scaling-stroke"
      style={{ strokeDasharray: 1000, strokeDashoffset: 1000, filter: "drop-shadow(0 0 4px rgba(193,127,89,.8))", animation: "vestaEcg 8s linear infinite" }} />
  </svg>
);

function MonitoringAi() {
  const [d, setD] = useState(null);
  const asId = useAsId();
  useEffect(() => {
    setD(null);
    api.get("/vesta/monitoring/ai-summary", { params: asId ? { as: asId } : {}, timeout: 90000 }).then((r) => setD(r.data)).catch(() => setD({ testo: null }));
  }, [asId]);
  if (d && !d.testo) return null;
  return (
    <div className="absolute bottom-3 right-3 z-10 flex w-[56%] items-start gap-2 rounded-xl border border-neutral-800 bg-black/60 p-2.5 backdrop-blur-md sm:bottom-6 sm:right-6 sm:w-[44%] lg:w-[36%]" data-testid="vesta-slot-monitoring-ai">
      <Sparkles className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${d ? "" : "animate-pulse"}`} style={{ color: d?.ok ? "#22c55e" : COPPER }} />
      <p className="line-clamp-4 text-[11px] leading-snug text-neutral-300 sm:text-xs" data-testid="vesta-slot-monitoring-ai-text">{d ? d.testo : "L'AI sta leggendo lo stato dei tuoi impianti…"}</p>
    </div>
  );
}

function MonitoringCounts() {
  const [k, setK] = useState(null);
  const asId = useAsId();
  useEffect(() => {
    setK(null);
    api.get("/vesta/monitoring/summary", { params: asId ? { as: asId } : {} }).then((r) => setK(r.data)).catch(() => {});
  }, [asId]);
  if (!k) return null;
  const rows = [["attivi", "Attivi", "#22c55e"], ["fermi", "Fermi", "#a3a3a3"], ["guasti", "Guasti", "#ef4444"]];
  return (
    <div className="absolute right-3 top-3 z-10 flex flex-col items-end gap-1 sm:right-4 sm:top-4" data-testid="vesta-slot-monitoring-counts">
      {rows.map(([key, label, c]) => (
        <span key={key} className="flex items-center gap-1.5 rounded-full border border-neutral-800 bg-black/60 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-300 backdrop-blur-md sm:px-2.5 sm:text-[11px]" data-testid={`vesta-slot-monitoring-${key}`}>
          <span className="h-2 w-2 rounded-full" style={{ background: c, boxShadow: `0 0 8px ${c}` }} />
          <b className="font-mono text-white">{k[key]}</b><span className="">{label}</span>
        </span>
      ))}
    </div>
  );
}

const WIDE = ["monitoring", "ai-vesta"];
const SlotCube = ({ s, i, onEnter, still }) => {
  const Icon = s.icon;
  const wide = WIDE.includes(s.key);
  return (
    <Link to={s.to || `/vesta/slot/${s.key}`} data-testid={`vesta-slot-${s.key}`} onClick={(e) => onEnter(e, s)}
      className={`group relative flex ${wide ? "col-span-full aspect-[2/1] lg:aspect-[4/1]" : "aspect-square"} flex-col justify-between overflow-hidden rounded-2xl border border-neutral-800 bg-neutral-950 p-5 transition-[border-color,box-shadow,transform] duration-300 ${still ? "" : "opacity-0 [animation:vestaIn_.5s_ease-out_forwards]"} hover:-translate-y-1 hover:border-[#C17F59] hover:shadow-[0_0_32px_-6px_rgba(193,127,89,.55)] sm:p-7`}
      style={{ animationDelay: `${i * 90}ms` }}>
      <span className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-40" style={{ background: COPPER }} />
      <span className="pointer-events-none absolute inset-y-0 left-0 w-2/3 opacity-0 [animation:vestaSweep_7.2s_linear_infinite]" data-testid={`vesta-slot-sweep-${s.key}`}
        style={{ background: "linear-gradient(100deg, transparent 0%, rgba(193,127,89,.10) 30%, rgba(193,127,89,.38) 50%, rgba(193,127,89,.10) 70%, transparent 100%)", animationDelay: `${0.6 + i * 1.2}s` }} />
      <span className="pointer-events-none absolute inset-0 rounded-2xl border opacity-0 [animation:vestaEdge_7.2s_linear_infinite]"
        style={{ borderColor: COPPER, boxShadow: "0 0 26px -6px rgba(193,127,89,.6)", animationDelay: `${0.6 + i * 1.2}s` }} />
      {s.key === "ai-vesta" && (
        <span className={`pointer-events-none absolute opacity-90 [perspective:900px] ${wide ? "top-1/2 -translate-y-1/2 -right-[6%] h-[150%] translate-x-[30%]" : "bottom-0 -right-[34%] w-[84%] translate-y-[12%]"}`}>
          <Coin wide={wide} />
        </span>
      )}
      {s.key === "bacheca" && <BachecaNews wide={wide} />}
      {s.key === "monitoring" && <><EcgLine /><MonitoringCounts /><MonitoringAi /></>}
      <Icon className="relative h-[1.8rem] w-[1.8rem] shrink-0 sm:h-9 sm:w-9" style={{ color: COPPER }} strokeWidth={1.6} />
      <div className={`relative ${s.key === "ai-vesta" ? "pr-[34%] sm:pr-[30%]" : s.key === "monitoring" ? "pr-[60%] sm:pr-[50%] lg:pr-[40%]" : ""}`}>
        <div className="font-heading text-base font-extrabold leading-tight text-white sm:text-xl">{s.title}</div>
        <p className={`mt-1 text-[11px] leading-snug text-neutral-400 sm:mt-1.5 sm:text-xs`} data-testid={`vesta-slot-desc-${s.key}`}>{s.desc}</p>
      </div>
    </Link>
  );
};

export default function VestaHome() {
  const me = useVestaMe();
  const base = useAsId() === DIREZ ? SLOTS.filter((x) => x.key === "monitoring") : SLOTS;
  const slots = me?.is_admin ? [...base, { key: "tariffario", title: "Tariffario Versione Pro", desc: "I prezzi della Versione Pro per fascia di potenza.", icon: Tag, to: "/vesta/tariffario" }] : base;
  const nav = useNavigate();
  const [portal, setPortal] = useState(null);
  const still = !!useLocation().state?.back;
  const onEnter = (e, s) => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    e.preventDefault();
    if (portal) return;
    const hb = (document.querySelector('[data-testid="vesta-viewas-bar"]') || document.querySelector('[data-testid="vesta-header"]'))?.getBoundingClientRect().bottom || 0;
    const vw = window.innerWidth, vh = window.visualViewport?.height || window.innerHeight;
    const m = vw < 640 ? 12 : 24;
    const target = { top: hb + m, left: m, width: vw - 2 * m, height: Math.max(200, vh - hb - 3 * m), borderRadius: 20 };
    setPortal({ slot: s, rect: e.currentTarget.getBoundingClientRect(), target });
    setTimeout(() => nav(s.to || `/vesta/slot/${s.key}`), 850);
  };
  return (
    <div className="min-h-screen bg-black text-white" data-testid="vesta-home">
      <style>{"@keyframes vestaIn{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}"
        + "@keyframes vestaSweep{0%{opacity:0;transform:translateX(-100%)}3%{opacity:1}30%{opacity:1}33.3%,100%{opacity:0;transform:translateX(150%)}}"
        + "@keyframes vestaEcg{0%{stroke-dashoffset:1000;opacity:1}60%{stroke-dashoffset:0;opacity:1}75%{stroke-dashoffset:0;opacity:0}100%{stroke-dashoffset:1000;opacity:0}}"
        + "@keyframes vestaCoinSpin{0%{transform:rotateY(0deg);animation-timing-function:ease-in-out}33.333%{transform:rotateY(360deg)}100%{transform:rotateY(360deg)}}"
        + "@keyframes vestaEdge{0%,100%{opacity:0}14%{opacity:.85}33.3%{opacity:0}}"
        + "@keyframes vestaBlink{0%,100%{opacity:1;text-shadow:0 0 14px rgba(193,127,89,.7)}50%{opacity:.78;text-shadow:0 0 4px rgba(193,127,89,.25)}}"
        + "@media (prefers-reduced-motion:reduce){[class*='vestaSweep'],[class*='vestaEdge'],[class*='vestaBlink']{animation:none!important}}"}</style>
      <VestaHeader />
      <main className="mx-auto max-w-5xl px-5 py-10 sm:px-10 sm:py-14">
        <div className="">
        <div className="mb-3 flex items-center gap-4 py-3"><div className="pl-3 text-[19.5px] font-extrabold tracking-[0.3em]"><HearthVesta /></div><VestaInfo /><span className="ml-auto"><VestaVoice /></span></div>
        <h1 className="mb-8 max-w-3xl font-heading text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl" data-testid="vesta-home-title">Per essere l’<span className="[animation:vestaBlink_3.2s_ease-in-out_infinite]" style={{ color: COPPER }} data-testid="vesta-blink-1pct">1%</span> devi fare quello che il 99% non fa.</h1>
        </div>
        <WelcomeBar />
        <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4" data-testid="vesta-slots">
          {slots.map((s, i) => <SlotCube key={s.key} s={s} i={i} onEnter={onEnter} still={still} />)}
        </div>
      </main>
      {portal && <PortalOverlay portal={portal} />}
    </div>
  );
}

const ChiaviSection = () => {
  const d = useChiavi("commerciale");
  return <><ChiaviMap d={d} kind="commerciale" /><BolletteReport /><InnerSlots slotKey="chiavi-in-mano" chiavi={d} /></>;
};
const TecniciSection = () => {
  const d = useChiavi("tecnica");
  return <><ChiaviMap d={d} kind="tecnica" /><TecniciWide d={d} /><InnerSlots slotKey="cassetta-attrezzi" /></>;
};

const COIN_LAYERS = 14;
const Coin = ({ wide }) => {
  const t = wide ? 18 : 12;
  const size = wide ? "h-full w-auto" : "w-full";
  return (
    <span className={`relative block [transform-style:preserve-3d] [animation:vestaCoinSpin_12s_linear_infinite] ${wide ? "h-full" : ""}`} data-testid="vesta-slot-coin">
      <img src="/vesta-coin-rame.png" alt="" aria-hidden="true" className={`relative max-w-none ${size}`} style={{ transform: `translateZ(${t / 2}px)` }} />
      {Array.from({ length: COIN_LAYERS }, (_, i) => (
        <img key={i} src="/vesta-coin-rame.png" alt="" aria-hidden="true" className={`absolute inset-0 max-w-none ${size}`}
          style={{ transform: `translateZ(${t / 2 - ((i + 1) * t) / (COIN_LAYERS + 1)}px)`, filter: "brightness(.5) sepia(.4)" }} />
      ))}
      <img src="/vesta-coin-rame.png" alt="" aria-hidden="true" className={`absolute inset-0 max-w-none ${size}`} style={{ transform: `translateZ(${-t / 2}px) rotateY(180deg)` }} />
    </span>
  );
};

export function VestaSlot() {
  const { key } = useParams();
  const asId = useAsId();
  const [sp, setSp] = useSearchParams();
  const s = SLOTS.find((x) => x.key === key);
  if (!s) return <Navigate to="/vesta/home" replace />;
  if (asId === DIREZ && key !== "monitoring") return <Navigate to="/vesta/slot/monitoring" replace />;
  const Icon = s.icon;
  return (
    <div className="min-h-screen bg-black text-white" data-testid="vesta-slot-page">
      <VestaHeader />
      <style>{"@keyframes vestaArrive{from{opacity:0;transform:scale(1.06);filter:blur(6px)}to{opacity:1;transform:none;filter:none}}"}</style>
      <main className="mx-auto max-w-4xl px-5 py-16 [animation:vestaArrive_.6s_ease-out] sm:px-10">
        <Link to="/vesta/home" replace state={{ back: true }} className="mb-10 inline-flex items-center gap-1 text-xs text-neutral-400 hover:text-white" data-testid="vesta-slot-back"><ArrowLeft className="h-3.5 w-3.5" /> Tutti i servizi</Link>
        <Icon className="mb-6 h-12 w-12" style={{ color: COPPER }} strokeWidth={1.6} />
        <h1 className="mb-3 font-heading text-4xl font-extrabold tracking-tight sm:text-5xl" data-testid="vesta-slot-title">{s.title}</h1>
        <p className="mb-8 text-neutral-400">{s.desc}</p>
        {s.key === "monitoring" ? <TorriMonitoring key={asId || "rete"} base="/vesta/monitoring" params={asId ? { as: asId } : {}} accent={COPPER} aiReport focus={sp.get("plant")} stato={sp.get("stato")} onFocusClear={() => sp.get("plant") && setSp({}, { replace: true })} /> : s.key === "chiavi-in-mano" ? <ChiaviSection /> : s.key === "cassetta-attrezzi" ? <TecniciSection /> : hasInner(s.key) ? <InnerSlots slotKey={s.key} /> : (
          <span className="inline-block rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-widest" style={{ borderColor: COPPER, color: COPPER }} data-testid="vesta-slot-soon">In arrivo</span>
        )}
      </main>
    </div>
  );
}
